// src/pages/RecipientPage.tsx
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCard } from "../hooks/useCard";
import { RecipientFlow } from "../components/recipient/RecipientFlow";
import { ArrowRight, Music } from "lucide-react";
import { ARCHETYPES, ArchetypeId } from "../constants/archetypes";
import logo from "../assets/img/logo.png";
import { RecipientPasscodeGate } from "../components/recipient/RecipientPasscodeGate";

export default function RecipientPage() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [showStartPrompt, setShowStartPrompt] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [passcodeUnlocked, setPasscodeUnlocked] = useState(false);

  const { card, markAsReceived, loading } = useCard(cardId);

  const handleStartExperience = () => {
    setShowStartPrompt(false);
    setHasStarted(true); // Set hasStarted to true when button is clicked
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Experience Not Found</h2>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-primary text-white rounded-xl"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const archetypeId = (card.archetype || "heartbeat") as ArchetypeId;
  const archConfig = ARCHETYPES[archetypeId];
  const primaryColor = card.primaryColor || archConfig?.defaultCardData.primaryColor || "#E11D48";
  const secondaryColor =
    card.secondaryColor || archConfig?.defaultCardData.secondaryColor || "#000000";

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Only render RecipientFlow if hasStarted is true */}
      {hasStarted && (!card.requiresPasscode || passcodeUnlocked) && (
        <RecipientFlow
          card={card}
          onComplete={() => {
            if (card.status === "draft") return;
            markAsReceived(card.id);
          }}
        />
      )}

      {/* Start Prompt */}
      <AnimatePresence>
        {showStartPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1, filter: "blur(20px)" }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
            style={{
              background: `radial-gradient(circle at center, #000 0%, ${secondaryColor} 100%)`,
            }}
          >
            {/* Cinematic Ambience */}
            <motion.div
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(circle at center, ${primaryColor}15 0%, transparent 70%)`,
              }}
            />

            {/* Atmospheric Particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: Math.random() * 2 + 1 + "px",
                    height: Math.random() * 2 + 1 + "px",
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: 0,
                  }}
                  animate={{
                    y: [0, -100],
                    opacity: [0, 0.3, 0],
                  }}
                  transition={{
                    duration: 10 + Math.random() * 10,
                    repeat: Infinity,
                    delay: Math.random() * 5,
                    ease: "linear",
                  }}
                />
              ))}
            </div>

            {/* Centerpiece */}
            <div className="relative z-10 flex flex-col items-center max-w-sm px-8">
              {/* Logo with Halo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="mb-12 relative flex items-center justify-center"
              >
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative z-10"
                >
                  <img
                    src={logo}
                    alt="LRNSY Logo"
                    className="w-20 h-20 md:w-24 md:h-24 object-contain brightness-110"
                  />
                </motion.div>

                {/* Ambient Halo */}
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 blur-3xl rounded-full scale-150"
                  style={{
                    background: `radial-gradient(circle, ${primaryColor}44 0%, transparent 70%)`,
                  }}
                />
              </motion.div>

              {/* Title Section */}
              <div className="text-center space-y-4 mb-16">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.4 }}
                  transition={{ delay: 0.5, duration: 1 }}
                  className="text-[10px] uppercase tracking-[0.5em] text-white"
                >
                  A special moment awaits
                </motion.p>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 1.2 }}
                  className="text-4xl md:text-5xl font-serif italic text-white font-light"
                >
                  For {card.recipientName}
                </motion.h2>

                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "40px" }}
                  transition={{ delay: 1.2, duration: 1.5 }}
                  className="h-px bg-white/20 mx-auto mt-6"
                />
              </div>

              {card.requiresPasscode && !passcodeUnlocked && (
                <RecipientPasscodeGate
                  cardId={card.id}
                  onUnlocked={() => setPasscodeUnlocked(true)}
                />
              )}
              {/* Begin Button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                onClick={handleStartExperience}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative flex flex-col items-center gap-4"
              >
                {/* <PrivacyTrustNote variant="recipient" /> */}

                <div
                  className="px-12 py-5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white transition-all group-hover:bg-white group-hover:text-black group-hover:border-transparent overflow-hidden"
                  style={{
                    boxShadow: `0 10px 30px -5px ${primaryColor}40`,
                  }}
                >
                  <div className="relative z-10 flex items-center gap-3">
                    <span className="text-xs uppercase tracking-[0.3em] font-medium">
                      Begin Experience
                    </span>
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>

                  {/* Subtle light sweep */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shimmer"
                    initial={false}
                  />
                </div>

                <div className="flex items-center gap-2 opacity-30 group-hover:opacity-60 transition-opacity">
                  <Music size={10} />
                  <span className="text-[9px] uppercase tracking-widest font-light">
                    Sound Recommended
                  </span>
                </div>
              </motion.button>
            </div>

            {/* Footer Signature */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              transition={{ delay: 2.5 }}
              className="absolute bottom-12"
            >
              <span className="text-[10px] tracking-[0.8em] text-white uppercase font-light">
                LRNSY
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
